import * as THREE from 'three';
import { ARENA_RADIUS, type Simulation, type Team } from '@moba/shared';

/** Radio de visión de cada tipo de unidad aliada (el doble que antes). */
const HERO_VISION = 20;
const MINION_VISION = 13;
const STRUCTURE_VISION = 18;
const NEXUS_VISION = 24;
/** Número máximo de focos de visión que el shader puede procesar. */
const MAX_SOURCES = 48;

export interface VisionSource {
  x: number;
  z: number;
  radius: number;
}

/** Focos de visión del equipo: héroes, minions y estructuras vivos. */
export function visionSources(sim: Simulation, team: Team): VisionSource[] {
  const sources: VisionSource[] = [];
  for (const hero of sim.heroes) {
    if (hero.alive && hero.team === team) sources.push({ x: hero.pos.x, z: hero.pos.z, radius: HERO_VISION });
  }
  for (const minion of sim.minions) {
    if (minion.alive && minion.team === team) sources.push({ x: minion.pos.x, z: minion.pos.z, radius: MINION_VISION });
  }
  for (const structure of sim.structures) {
    if (!structure.alive || structure.team !== team) continue;
    sources.push({
      x: structure.pos.x,
      z: structure.pos.z,
      radius: structure.kind === 'nexus' ? NEXUS_VISION : STRUCTURE_VISION,
    });
  }
  return sources;
}

/** ¿El punto (x, z) cae dentro de algún foco de visión? */
export function isVisible(sources: readonly VisionSource[], x: number, z: number, padding = 0): boolean {
  for (const source of sources) {
    const radius = source.radius + padding;
    const dx = x - source.x;
    const dz = z - source.z;
    if (dx * dx + dz * dz <= radius * radius) return true;
  }
  return false;
}

/**
 * Niebla de guerra en el suelo: un plano oscuro sobre la arena al que el shader
 * le abre agujeros suaves alrededor de cada foco de visión aliado.
 */
export class FogOfWar {
  readonly mesh: THREE.Mesh;

  private readonly vectors = Array.from({ length: MAX_SOURCES }, () => new THREE.Vector3());
  private readonly uniforms = {
    uVision: { value: this.vectors },
    uCount: { value: 0 },
    uOpacity: { value: 0.84 },
  };

  constructor(scene: THREE.Scene) {
    const size = ARENA_RADIUS * 2 + 40;
    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      vertexShader: /* glsl */ `
        varying vec2 vWorld;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = world.xz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: /* glsl */ `
        #define MAX_SOURCES ${MAX_SOURCES}
        uniform vec3 uVision[MAX_SOURCES];
        uniform int uCount;
        uniform float uOpacity;
        varying vec2 vWorld;

        void main() {
          float visibility = 0.0;
          for (int i = 0; i < MAX_SOURCES; i++) {
            if (i >= uCount) break;
            vec3 source = uVision[i];
            float dist = distance(vWorld, source.xy);
            visibility = max(visibility, 1.0 - smoothstep(source.z - 1.4, source.z, dist));
          }
          float alpha = uOpacity * (1.0 - visibility);
          if (alpha < 0.004) discard;
          gl_FragColor = vec4(0.012, 0.035, 0.07, alpha);
        }
      `,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.y = 0.04;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  update(sources: readonly VisionSource[]): void {
    const count = Math.min(sources.length, MAX_SOURCES);
    for (let i = 0; i < count; i++) {
      this.vectors[i].set(sources[i].x, sources[i].z, sources[i].radius);
    }
    this.uniforms.uCount.value = count;
    this.mesh.visible = count > 0;
  }
}
